import React, { useMemo, useState } from 'react';
import { Download, Clock } from 'lucide-react';
import { formatDateTime } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';
import { getTransactionDisplayAmount, getTransactionDisplayCurrency } from '../../utils/transactionDisplay';

const ActivityReportTab = ({ report, loading, onDownloadCsv }) => {
  const entries = useMemo(() => report?.entries || [], [report]);
  const { t, locale } = useLanguage();
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 3;

  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    return formatDateTime(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }, locale);
  };

  const formatAmount = (amount) => {
    const value = parseFloat(amount || 0);
    return Number.isFinite(value) ? value.toFixed(2) : '0.00';
  };

  const getCategoryLabel = (category) => {
    const key = `activityReport.category.${category}`;
    const translated = t(key);
    return translated === key ? category : translated;
  };

  const getActionLabel = (action) => {
    const map = {
      deposit: 'deposit',
      withdrawal: 'withdrawal',
      purchase: 'purchase',
      sale: 'sale',
      loan_borrow: 'loanBorrow',
      loan_lend: 'loanLend',
      gameweek_prize: 'gameweekPrize'
    };
    const mapped = map[action] || action;
    const key = `activityReport.action.${mapped}`;
    const translated = t(key);
    return translated === key ? action : translated;
  };

  const paginatedEntries = entries.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const totalPages = Math.max(1, Math.ceil(entries.length / ITEMS_PER_PAGE));

  return (
    <div className="space-y-6">
      <div className="football-card glass-football p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-400" />
            <div>
              <h2 className="text-2xl font-bold text-[#1e293b]">{t('activityReport.title')}</h2>
              <p className="text-sm text-gray-400">{t('activityReport.last7Days')}</p>
            </div>
          </div>
          <button
            onClick={onDownloadCsv}
            disabled={!entries.length}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              entries.length
                ? 'bg-yellow-400 text-black hover:bg-yellow-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Download className="w-4 h-4" />
            {t('activityReport.downloadCsv')}
          </button>
        </div>
      </div>

      <div className="football-card glass-football p-6">
        {loading ? (
          <div className="text-center py-10">
            <div className="football-spinner mx-auto"></div>
            <p className="mt-4 text-gray-400">{t('activityReport.loading')}</p>
          </div>
        ) : entries.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedEntries.map((entry) => (
                <div key={`${entry.category}-${entry.id}`} className="p-4 rounded-lg border-2 border-gray-500/30 bg-gray-500/5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-gray-400">{formatDate(entry.createdAt)}</p>
                      <p className="text-white font-semibold">
                        {getCategoryLabel(entry.category)} • {getActionLabel(entry.action)}
                      </p>
                      <p className="text-xs text-gray-400">{entry.description || '-'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400 font-semibold">
                        {formatAmount(getTransactionDisplayAmount(entry))} {getTransactionDisplayCurrency(entry)}
                      </p>
                      <p className="text-xs text-gray-400">{entry.status}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {entries.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    currentPage === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-400 text-black hover:bg-yellow-500'
                  }`}
                >
                  {t('common.previous')}
                </button>
                <span className="text-gray-400 text-sm">
                  {t('common.pageOf', { page: currentPage + 1, total: totalPages })}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    currentPage >= totalPages - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-400 text-black hover:bg-yellow-500'
                  }`}
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-gray-400">
            {t('activityReport.empty')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityReportTab;
